import type { PlaceSearchResult, RequestOptions } from '../arcticapi';

export interface CurrentCoordinates {
  latitude: number;
  longitude: number;
}

export interface CurrentLocationResult extends CurrentCoordinates {
  label: string;
}

export interface PositionProvider {
  getCurrentPosition(): Promise<CurrentCoordinates>;
}

export interface CurrentLocationApiClient {
  reverseGeocode(
    location: string,
    options?: RequestOptions,
  ): Promise<{ commonName: string } | null>;
}

export interface LookupCurrentLocationDependencies {
  positionProvider: PositionProvider;
  apiClient?: CurrentLocationApiClient;
}

export const DEFAULT_CURRENT_LOCATION_LABEL = 'Current Location';

export function formatCurrentLocationCoordinates({
  latitude,
  longitude,
}: CurrentCoordinates): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

export function currentLocationToPlaceSearchResult(
  currentLocation: CurrentLocationResult,
): PlaceSearchResult {
  return {
    id: `current-location:${currentLocation.latitude},${currentLocation.longitude}`,
    type: 'place',
    commonName: currentLocation.label,
    localityName: null,
    location: {
      type: 'Point',
      coordinates: [currentLocation.longitude, currentLocation.latitude],
    },
  };
}

export function createBrowserPositionProvider(
  geolocation: Geolocation | undefined = getBrowserGeolocation(),
): PositionProvider {
  return {
    getCurrentPosition: () => {
      if (!geolocation) {
        return Promise.reject(
          new Error('Current location is not supported on this device.'),
        );
      }

      return new Promise<CurrentCoordinates>((resolve, reject) => {
        geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            reject(mapGeolocationError(error));
          },
        );
      });
    },
  };
}

export async function lookupCurrentLocation(
  dependencies: LookupCurrentLocationDependencies,
  options: { signal?: AbortSignal } = {},
): Promise<CurrentLocationResult> {
  const coordinates = await dependencies.positionProvider.getCurrentPosition();

  try {
    const label = await reverseGeocodeCurrentLocation(
      dependencies.apiClient,
      coordinates,
      options.signal,
    );

    return {
      ...coordinates,
      label: label ?? DEFAULT_CURRENT_LOCATION_LABEL,
    };
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    return {
      ...coordinates,
      label: formatCurrentLocationCoordinates(coordinates),
    };
  }
}

function getBrowserGeolocation(): Geolocation | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return navigator.geolocation;
}

async function reverseGeocodeCurrentLocation(
  apiClient: CurrentLocationApiClient | undefined,
  coordinates: CurrentCoordinates,
  signal: AbortSignal | undefined,
): Promise<string | null> {
  if (!apiClient) {
    return null;
  }

  const place = await apiClient.reverseGeocode(
    `${coordinates.latitude},${coordinates.longitude}`,
    { signal },
  );

  return place?.commonName ?? null;
}

function mapGeolocationError(error: GeolocationPositionError): Error {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new Error('Location access was denied.');
    case error.POSITION_UNAVAILABLE:
      return new Error('Unable to determine your current location.');
    case error.TIMEOUT:
      return new Error('Location request timed out.');
    default:
      return new Error('Unable to determine your current location.');
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
